import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnTable } from 'arquero'
import { CELL_GROUPS_CATEGORY_KEY } from 'components/cell-visualizations/Compare/useDataCategory'
import {
  CELL_IMAGE_NORMAL_SIZE,
  CELL_IMAGE_NORMAL_SPACING,
} from 'components/cell-visualizations/shared'
import { DifferentialFeaturesTopFeatureCount } from 'components/cell-visualizations/tsv/types'
import _ from 'lodash'
import { SessionPostData, TaskStatus } from 'utils/api'
import { getSimpleReducerMethods } from 'utils/reduxHelpers'
import { DatasetItem } from '../types/DatasetItem'
import {
  CellDataField,
  CellImagesFilter,
  CellInfoDimension,
  CellInfoGroups,
  DifferentialFeaturesComparison,
  ScopeMode,
  MergedMorphotype,
  PaginationParams,
  PinnedMorphotype,
  ProjectionParams,
  TopFeature,
  ZoomThreshold,
} from './types'

export const imageCountDefault = 10
export const defaultPagination = { page: 0, cellsPerPage: imageCountDefault }

export interface CellsByGroup {
  groupId: number
  groupName: string
  cellIds: string[]
}

export enum FullscreenPanelType { // Used by the CellSelectionDrawer component in the CellVisualizationState
  NONE = 'none',
  DIFFERENTIAL_FEATURES = 'differential_features',
}

export enum MorphotypePanelDisplayMode {
  CELL_GROUPS = 'cell_groups',
  MERGE = 'merge',
  DIFFERENTIAL_FEATURES = 'differential_features',
}

// Keep this in sync with the initial state that's the next variable below
//
// Also if you add any temporary variables that should not be saved, please update
// saveVersionConfig in useSessionApi.ts
export interface CellVisualizationsState {
  /**
   * Indicates the version of this state. Used for matching stateVersion to logic in the backend
   * @important Whenever CellVisualizationsState is changed, please increment stateVersion
   */
  // Keep this in sync with the saveVersionConfig function in useSessionApi.ts
  // That function omits specific fields from this state that should not be persisted when saving
  stateVersion: 1

  // Overview information about this session
  // Name is now stored in sessionData.name (top-level field from API)
  // projectCode is entered by the user when they save
  // fileName is the name of the file that was loaded
  fileName?: string
  projectCode?: string
  plotImgSrc?: string // holds screenshot of the plot for the session

  /**
   * Arquero DataFrame singleton containing cell data in columnar format.
   * Created from Arrow IPC buffer (200 MB for 100k cells).
   * Stored in Redux to prevent creating 100+ duplicate instances.
   * All components share this single DataFrame reference.
   *
   * Note: The original arrowBuffer is not stored to avoid memory duplication.
   * Arrow's internal parsed vectors (~200 MB) are already inside the DataFrame.
   */
  dataFrame?: ColumnTable

  // Name of the classifier, if the user trained a classifier using this version of the session
  classifierName?: string

  // Flags used to manage state within the applicaion
  dataRevision: number
  isDirty: boolean

  // Pre-filtering state
  morphotypesToRemove?: PinnedMorphotype[]

  // Morphotype format version for migration tracking
  // Version 1 (or undefined): Old format with filter dependencies and tile restrictions
  // Version 2: New format with global geometric selections
  morphotypeFormatVersion?: number

  // Color-by / Filter-by UX state (left panel)
  showColorBy?: boolean
  cellInfoGroups: CellInfoGroups
  selectedCellInfoGroupName: keyof CellInfoGroups

  // Display images settings and images to show at different zoom levels
  cellImagesFilter: CellImagesFilter
  zoomThresholds?: ZoomThreshold[]

  // Plot display type
  plotDisplayType: 'scatter' | 'density'

  // CellSelectionDrawer (right side panels) state management
  showMorphotypes?: boolean
  showCompare?: boolean
  showTopUpPanel?: boolean
  fullscreenPanel?: FullscreenPanelType
  morphotypePanelDisplayMode?: MorphotypePanelDisplayMode


  // Morphotypes user has pinned (part of right panel)
  pinnedCells?: PinnedMorphotype[]
  mergedPinnedCells?: MergedMorphotype[] // holds merged morphotypes, if the user's merged

  // Morphotypes panel, differential features component UX state.
  // Stores the results list from comparisons
  differentialFeaturesComparisons?: DifferentialFeaturesComparison[]
  selectedDifferentialFeaturesTaskId?: number
  
  // Differential features form state (scope settings)
  differentialFeaturesScope?: {
    useSameScope: boolean
    scopeMode1: ScopeMode
    scopeValues1: string[]
    scopeMode2: ScopeMode
    scopeValues2: string[]
  }

  // Morphotypes panel, cells selected for image export
  exportCellMorphotype: CellsByGroup[]

  // Compare tab state
  groupCompare: {
    selectedFeatures: CellDataField[]
    selectedDataCategory: string // Datafield.label value
    selectedDataFields: { [key: string]: string[] } // key is a DataField.label value,
    dragDropEnabled?: boolean // enable/disable drag and drop functionality
  }
  selectedComparisonDimensions?: CellInfoDimension[]

  // Selected demo dataset UX state
  selectedDataset?: DatasetItem
  differentialFeaturesTopFeatureCountMap ?:DifferentialFeaturesTopFeatureCount

  // Session data, without the version_config which is stored elsewhere in this slice
  // As of SWT-1312, sessionData.name is now the authoritative name field in a cleaner,
  // top-level location on the frontend and backend.
  //
  // We no longer store name in version_config
  sessionData?:SessionPostData
  projection_params?:ProjectionParams
  max_cell_count?:number
  leiden_resolution?:number


  // Session-based UX state
  overlapWarningShown?: boolean // Track if overlap warning has been shown this session

  // Highlighted Leiden cluster for hover effects in legend
  highlightedLeidenCluster?: string

}
// Keep the CellvIsualizationSlice above in sync with the initial state that's below
//
// Also if you add any temporary variables that should not be saved, please update
// saveVersionConfig in useSessionApi.ts


// Keep this in sync with the type definition just above this
export const cellVisualizationsInitialState: CellVisualizationsState = {
  stateVersion: 1,

  // Overview information about this session
  // Name is now stored in sessionData.name (top-level field from API)
  // projectCode is entered by the user when they save
  // fileName is the name of the file that was loaded
  fileName: undefined,
  projectCode: undefined,

  /**
   * Arquero DataFrame singleton - prevents creating 100+ duplicate instances
   */
  dataFrame: undefined,

  // Flags used to manage state within the application
  dataRevision: 1,
  isDirty: false,

  // Pre-filtering state
  morphotypesToRemove: [],

  // Color-by / Filter-by UX state
  showColorBy: undefined,
  cellInfoGroups: {},
  selectedCellInfoGroupName: '',

  // Display Images settings
  cellImagesFilter: {
    displayImages: false,
    imageSize: CELL_IMAGE_NORMAL_SIZE,
    spacingAdjust: CELL_IMAGE_NORMAL_SPACING,
  },
  zoomThresholds: undefined,

  // Plot display type
  plotDisplayType: 'scatter',

  // CellSelectionDrawer (right side panels) state management
  showMorphotypes: undefined,
  showCompare: undefined,
  showTopUpPanel: undefined,
  fullscreenPanel: FullscreenPanelType.NONE,
  morphotypePanelDisplayMode: MorphotypePanelDisplayMode.CELL_GROUPS,

  // Morphotypes user has pinned (part of right panel)
  pinnedCells: [],
  mergedPinnedCells: undefined,

  // Morphotypes panel, differential features component UX state
  differentialFeaturesComparisons: [],
  selectedDifferentialFeaturesTaskId: undefined, // if in full screen mode, this is the taskId to show in detail
  differentialFeaturesScope: undefined, // Last used scope settings for the form

  // Morphotypes panel, cells selected for image export
  exportCellMorphotype: [],

  // Compare tab state
  groupCompare: {
    selectedFeatures: [],
    selectedDataCategory: CELL_GROUPS_CATEGORY_KEY,
    selectedDataFields: {},
    dragDropEnabled: false,
  },
  selectedComparisonDimensions: undefined,

  // Selected demo dataset UX state
  selectedDataset: undefined,
  differentialFeaturesTopFeatureCountMap :{},
  sessionData:undefined,
  projection_params:{},
  max_cell_count:undefined,
  leiden_resolution:undefined,
  overlapWarningShown: false,
  highlightedLeidenCluster: undefined,
  morphotypeFormatVersion: undefined,

}

export const cellVisualizationsSlice = createSlice({
  name: 'cellVisualizations',
  initialState: cellVisualizationsInitialState,
  reducers: {
    ...getSimpleReducerMethods(cellVisualizationsInitialState),
    // Custom setName to update sessionData.name instead of a top-level name field
    setName: (state, action: PayloadAction<string>) => {
      if (!state.sessionData) {
        state.sessionData = {} as SessionPostData
      }
      state.sessionData.name = action.payload
    },
    setMorphotypeToRemoveHighlight: (
      state,
      action: PayloadAction<{ id: number; shouldHighlight: boolean }>
    ) => {
      const { id, shouldHighlight } = action.payload
      const morphotype = state.morphotypesToRemove?.find((m) => m.id === id)
      if (morphotype) {
        morphotype.isHighlighted = shouldHighlight
      }
    },
    setGroupCompare(
      state,
      action: PayloadAction<{
        groupCompare: CellVisualizationsState['groupCompare']
        eventsManager: {
          sendCompareByEvent(
            dataCategory: string,
            selectedComparisionOptions: CellDataField[],
            dataFieldsToCompare: { [key: string]: string[] }
          ): void
        }
      }>
    ) {
      const { groupCompare, eventsManager } = action.payload
      state.groupCompare = groupCompare
      state.isDirty = true
      eventsManager.sendCompareByEvent(
        groupCompare.selectedDataCategory,
        groupCompare.selectedFeatures,
        groupCompare.selectedDataFields
      )
    },
    setGroupCompareDragDropEnabled(state, action: PayloadAction<boolean>) {
      // Ensure groupCompare object has dragDropEnabled property
      if (!state.groupCompare) {
        state.groupCompare = {
          selectedFeatures: [],
          selectedDataCategory: CELL_GROUPS_CATEGORY_KEY,
          selectedDataFields: {},
          dragDropEnabled: false,
        }
      }
      if (state.groupCompare.dragDropEnabled === undefined) {
        state.groupCompare.dragDropEnabled = false
      }
      state.groupCompare.dragDropEnabled = action.payload
      state.isDirty = true
    },
    /**
     * Only add cell ids to the group that don't already exist, if already exists remove
     * @param state
     * @param action action.payload - Contains an object groupId, cellId and groupName
     */
    appendCellToExport: (
      state,
      action: PayloadAction<{ groupId: number; cellId: string; groupName: string }>
    ) => {
      const { groupId, cellId, groupName } = action.payload

      const groupIndex = state.exportCellMorphotype.findIndex((morphotype) => {
        return morphotype.groupId === groupId
      })

      // Group already exists
      if (groupIndex !== -1) {
        const currentGroup = state.exportCellMorphotype[groupIndex]
        // Check if the Cell Id exists in the group
        const cellIndex = currentGroup.cellIds.findIndex((cell) => {
          return cell === cellId
        })

        // Cell Id already exist, remove from list (uncheck)
        if (cellIndex !== -1) {
          if (currentGroup.cellIds.length <= 10) {
            currentGroup.cellIds.splice(cellIndex, 1)
          }
          // If no cells exists, remove the group
          if (currentGroup.cellIds.length === 0) {
            state.exportCellMorphotype.splice(groupIndex, 1)
          }
        } else if (currentGroup.cellIds.length < 10) {
          // Append the Cell Id to the group
          currentGroup.cellIds.push(cellId)
        }
      } else {
        // Create the group and append the Cell Id
        state.exportCellMorphotype.push({ groupId, groupName, cellIds: [cellId] })
      }
    },
    setGroupCompareSelectedGroups(state, action: PayloadAction<number[]>) {
      state.pinnedCells = state.pinnedCells?.map((x) => ({
        ...x,
        isInSelectedGroup: action.payload.includes(x.id),
      }))
      state.isDirty = true
    },
    incrementDataRevision(state) {
      state.dataRevision += 1
    },
    setCellImagesFilter: (
      state,
      action: PayloadAction<{
        cellImagesFilter: CellImagesFilter
        eventsManager: { sendDisplayImagesByEvent(_: CellImagesFilter): void }
      }>
    ) => {
      const { cellImagesFilter, eventsManager } = action.payload
      eventsManager.sendDisplayImagesByEvent(cellImagesFilter)
      state.cellImagesFilter = cellImagesFilter
    },
    /**
     * Replaces the current state with the given payload.
     * @param state Current state
     * @param action action.payload contains the new state to apply
     *    Any fields that are missing will be set to their default initial value
     */
    setStore(state, action: PayloadAction<Partial<CellVisualizationsState>>) {
      // Using "state = action.payload" throws an error so we resort to this
      const newStore = action.payload
      _.keys(state).forEach((k) => {
        const key = k as keyof typeof state
        const val = newStore[key] ?? cellVisualizationsInitialState[key]

        ;(state[key] as typeof val) = val
      })
      state.isDirty = false
    },
    /** Clears state.  Normally used when you leave the cell visualization session
     * to avoid keeping around stale state the next time you create a new session or
     * open a different session
     */
    clearStore(state) {
      _.keys(state).forEach((k) => {
        const key = k as keyof typeof state
        const val = cellVisualizationsInitialState[key]
        ;(state[key] as typeof val) = val
      })
    },

    setPinnedGroupValue: (
      state,
      action: PayloadAction<{
        pinnedGroupId: number
        key: keyof PinnedMorphotype
        value: boolean | string | PaginationParams
      }>
    ) => {
      const { pinnedGroupId, key, value } = action.payload
      state.pinnedCells = state.pinnedCells?.map((pinnedCell) =>
        pinnedCell.id === pinnedGroupId
          ? {
              ...pinnedCell,
              [key]: value,
            }
          : pinnedCell
      )
    },
    setPinnedGroupPercentageScope: (
      state,
      action: PayloadAction<{
        pinnedGroupId: number
        percentageScope?: 'entire_session' | 'current_filters'
        breakdownType?: 'RUN_ID' | 'SAMPLE_ID' | 'SAMPLE_TYPE'
      }>
    ) => {
      const { pinnedGroupId, percentageScope, breakdownType } = action.payload
      state.pinnedCells = state.pinnedCells?.map((pinnedCell) =>
        pinnedCell.id === pinnedGroupId
          ? {
              ...pinnedCell,
              ...(percentageScope !== undefined && { percentageScope }),
              ...(breakdownType !== undefined && { breakdownType }),
            }
          : pinnedCell
      )
    },
    setPinnedCells: (
      state,
      action: PayloadAction<{
        pinnedCells: PinnedMorphotype[]
        eventsManager: { sendPinnedCellsEvent(_: PinnedMorphotype): void }
      }>
    ) => {
      const { pinnedCells, eventsManager } = action.payload
      const recentlyAddedPin = pinnedCells[0]
      if (recentlyAddedPin) eventsManager.sendPinnedCellsEvent(recentlyAddedPin)
      state.pinnedCells = pinnedCells
      state.isDirty = true
    },
    setMergedPinnedCells: (
      state,
      action: PayloadAction<{
        pinnedCells: MergedMorphotype[]
        eventsManager: {
          sendMergedPinnedCellsEvent(_: Pick<PinnedMorphotype, 'id' | 'cells'>[]): void
        }
      }>
    ) => {
      const { pinnedCells, eventsManager } = action.payload
      eventsManager.sendMergedPinnedCellsEvent(pinnedCells)
      state.mergedPinnedCells = pinnedCells
      state.isDirty = true
    },
    addPinnedMorphotype: (state, action: PayloadAction<PinnedMorphotype>) => {
      state.pinnedCells = state.pinnedCells
        ? [action.payload, ...state.pinnedCells]
        : [action.payload]
      state.isDirty = true
    },
    clearExportMorphotype: (state) => {
      state.exportCellMorphotype = []
    },
    deletePinnedGroup: (
      state,
      action: PayloadAction<{
        targetPinnedCellId: number
        eventsManager: { sendDeletePinEvent(_: string): void }
      }>
    ) => {
      const { targetPinnedCellId, eventsManager } = action.payload
      const deletedPin = state.pinnedCells?.filter((x) => x.id === targetPinnedCellId)
      if (deletedPin && deletedPin.length > 0) eventsManager.sendDeletePinEvent(deletedPin[0].name)
      else eventsManager.sendDeletePinEvent(`pinnedCellId ${targetPinnedCellId}`)
      state.pinnedCells = state.pinnedCells?.filter((x) => x.id !== targetPinnedCellId)
      state.isDirty = true
    },

    /** Adds a comparison to the top of the differential features comparison list */
    addDifferentialFeaturesComparison: (
      state,
      action: PayloadAction<DifferentialFeaturesComparison>
    ) => {
      if (state.differentialFeaturesComparisons === undefined) {
        state.differentialFeaturesComparisons = []
      }
      state.differentialFeaturesComparisons.unshift(action.payload)
    },

    /** Deletes a comparison in the differential features result list by taskId */
    deleteDifferentialFeaturesComparisonByTaskId: (
      state,
      action: PayloadAction<{
        taskId: number
      }>
    ) => {
      const { taskId } = action.payload
      state.differentialFeaturesComparisons = state.differentialFeaturesComparisons?.filter(
        (item) => item.taskId !== taskId
      )
    },

    /** Updates the results of a differential features comparison, by taskId */
    updateDifferentialFeaturesComparison: (
      state,
      action: PayloadAction<{
        taskId: number
        status: TaskStatus
        topFeatures?: TopFeature[]
        cellCounts?: {
          group1Count: number
          group2Count: number
        }
      }>
    ) => {
      const { taskId, status, topFeatures, cellCounts } = action.payload
      state.differentialFeaturesComparisons = state.differentialFeaturesComparisons?.map((item) =>
        item.taskId === taskId ? { ...item, status, topFeatures, cellCounts } : item
      )
    },
    setDifferentialFeaturesTopFeatureCount: (state, action) => {
      const { taskId, count } = action.payload
      if (state.differentialFeaturesTopFeatureCountMap) {
        state.differentialFeaturesTopFeatureCountMap[taskId] = count
      }
    },
    setDifferentialFeaturesScope: (
      state,
      action: PayloadAction<{
        useSameScope: boolean
        scopeMode1: ScopeMode
        scopeValues1: string[]
        scopeMode2: ScopeMode
        scopeValues2: string[]
      }>
    ) => {
      state.differentialFeaturesScope = action.payload
    },
    setSessionData: (state, action: PayloadAction<SessionPostData>) => {
      state.sessionData = action.payload
    },
    setOverlapWarningShown: (state, action: PayloadAction<boolean>) => {
      state.overlapWarningShown = action.payload
    },
  },
})

export const CellVisualizationsActions = cellVisualizationsSlice.actions
export const CellVisualizationsReducer = cellVisualizationsSlice.reducer
